/* eslint-disable max-lines */
import { createApp } from "vue";
import { createPinia } from "pinia";
import { readCookie } from "common/utils/cookie";
import { track as mixpanelTrack } from "jiffy-analytics";
import { waitForWindowVariable } from "common/utils/ui/wait_for";
import clickOutside from "components/jiffy/vue-cart/filters/clickOutside";
import i18n from "../vue-cart/vue-i18n";
import ProductDetailsDtf from "./ProductDetailsDtf.vue";
import { useDtfStore } from "./store";
import { GANG_DEFAULT_VALUES, INCHES_PER_FOOT } from "./common/constants";

const track = (event, payload) => {
  if (window.mixpanel) {
    mixpanelTrack(event, payload);
  } else {
    waitForWindowVariable("mixpanel", () => mixpanelTrack(event, payload));
  }
};

/* eslint-disable max-lines-per-function */
const initProductDetailsDtf = () => {
  const ProductDetailsDtfWrapper = createApp({});
  ProductDetailsDtfWrapper.use(createPinia());
  const $wrapper = document.getElementById("vue-product-details-dtf");
  const {
    variantId,
    productId,
    productSlug,
    catalogNumber,
    favorites,
    preCutPrice,
    preCutEnabled,
    printAsIs,
    sticker,
    pdpType,
    showCta,
    showPromoModal,
    showFirstStickerPromoModal,
    dtfMaxWidth,
    dtfMaxHeight,
    dtfMinWidth,
    dtfMinHeight,
    mobile,
    printingServiceName,
    spangleVersionIdentifiers,
    isSpangle,
    spangleColors,
    businessLine,
    isPuff,
    isProColor,
    isSticker,
    isStickerSample,
    isLetters,
    experiments,
    isJerseyNumbers,
    preCutDuration,
    dtfCategory,
    preCutUpsellThreshold,
    isGoldFoil,
    allowForceBgRemove,
    canvaDtfEnabled,
    forcePdpTransfersRedraw,
    enableRedraw,
    canvaReviewer,
    showMultiSize,
    gangSheetBuilderShopId,
    showGangSheetBuilderFeature,
    isGangSheetBuilder,
    gangSheetBuilderVariants,
    isDtfSheet,
    gangSheetBuilderSizes,
    gangSheetBuilderCarousels,
    cachedArtworkPrices,
    reorderEnabled
  } = $wrapper.dataset;

  const urlParams = new URLSearchParams(window.location.search);
  const rawArtworkIds = urlParams.get("uploaded_artwork_ids") || readCookie("uploaded_artwork_ids");
  const buyAsStickerArtworkIds = urlParams.get("buy_as_sticker_artwork_ids");
  const artworkWidth = urlParams.get("artwork_width");
  const artworkHeight = urlParams.get("artwork_height");
  const artworkPaddingSize = urlParams.get("artwork_padding_size");
  const artworkBgColor = urlParams.get("artwork_bg_color");
  const artworkColor = urlParams.get("artwork_color");
  const artworkShape = urlParams.get("artwork_shape");
  const processArtworkParam = urlParams.get("process_artwork");
  const processArtwork = processArtworkParam !== null ? processArtworkParam === "true" : true;
  const urlPreCut = urlParams.get("pre_cut");
  const orderNumber = urlParams.get("buy_all_from_order");
  const fileContentType = urlParams.get("file_content_type");
  const sourceParam = urlParams.get("source");
  const trackSpecificStickerLink = urlParams.get("track_specific_sticker_link");
  const trackOrderStickersLink = urlParams.get("order_stickers_link");
  const userType = readCookie("name") ? "signed_in" : "guest_user";
  const jerseyNumbers = isJerseyNumbers === "true";
  const letterNumberHeight = urlParams.get("height");
  const letterNumberStyle = urlParams.get("style");
  const letterNumberColor = urlParams.get("color");
  const letterNumberMaxLength = urlParams.get("max_length");
  const letterNumberFont = urlParams.get("font");
  const rawArtworkUrls = urlParams.get("artwork_urls");
  const designId = urlParams.get("design_id");
  let options = {};
  ProductDetailsDtfWrapper.component("ProductDetailsDtf", ProductDetailsDtf);
  ProductDetailsDtfWrapper.use(i18n);
  ProductDetailsDtfWrapper.directive("click-outside", clickOutside());

  const store = useDtfStore();
  store.setShowPromoModal(showPromoModal === "true");
  store.setShowFirstStickerPromoModal(showFirstStickerPromoModal === "true");
  store.setShowCTA(showCta === "true");
  store.setVariantId(variantId);
  store.setPreCutPrice(preCutPrice);
  store.setPreCutEnabled(JSON.parse(preCutEnabled));
  store.setProductId(productId);
  store.setProductSlug(productSlug);
  store.setFavorites(favorites);
  store.setPdpType(pdpType);
  store.setCatalogNumber(catalogNumber);
  store.setPrintAsIs(JSON.parse(printAsIs));
  store.setSticker(JSON.parse(sticker));
  store.setDtfMaxWidth(Number(dtfMaxWidth));
  store.setDtfMaxHeight(Number(dtfMaxHeight));
  store.setDtfMinWidth(Number(dtfMinWidth));
  store.setDtfMinHeight(Number(dtfMinHeight));
  store.setUserType(userType);
  store.setIsMobile(mobile === "true");
  store.setPrintingServiceName(printingServiceName);
  store.setSpangleAlgorithms(JSON.parse(spangleVersionIdentifiers || "[]"));
  store.setCachedArtworkPrices(JSON.parse(cachedArtworkPrices));
  store.setIsSpangle(isSpangle === "true");
  store.setIsProColor(isProColor === "true");
  store.setIsLetters(isLetters === "true");
  store.setSpangleColors(JSON.parse(spangleColors));
  store.setBusinessLine(businessLine);
  store.setIsPuff(isPuff === "true");
  store.setIsSticker(isSticker === "true");
  store.setIsStickerSample(isStickerSample === "true");
  store.setExperiments(JSON.parse(experiments));
  store.setIsJerseyNumbers(jerseyNumbers);
  store.setPreCutDuration(preCutDuration);
  store.setDtfCategory(dtfCategory);
  store.setPreCutUpsellThreshold(preCutUpsellThreshold);
  store.setIsGoldFoil(isGoldFoil === "true");
  store.setAllowForceBgRemove(allowForceBgRemove === "true");
  store.setCanvaDtfEnabled(canvaDtfEnabled === "true");
  store.setForcePdpTransfersRedraw(forcePdpTransfersRedraw === "true");
  store.setEnableRedraw(enableRedraw === "true");
  store.setCanvaReviewer(canvaReviewer === "true");
  store.setShowMultiSize(showMultiSize === "true");
  store.setReorderEnabled(reorderEnabled === "true");
  /* eslint-disable camelcase, no-undef */
  store.setGangSheetBuilder(typeof gs_builder !== "undefined" ? gs_builder : null);
  /* eslint-enable camelcase, no-undef */
  store.setGangSheetBuilderShopId(gangSheetBuilderShopId);
  store.setShowGangSheetBuilderFeature(JSON.parse(showGangSheetBuilderFeature || false));
  store.setIsDtfSheet(isDtfSheet === "true");
  store.setGangSheetBuilderSizes(JSON.parse(gangSheetBuilderSizes));

  if (isGangSheetBuilder === "true") {
    const variants = JSON.parse(gangSheetBuilderVariants);
    let defaultVariant = variants[Object.keys(variants)[0]];

    if (urlParams.get("v")) {
      const selectedVariant = Object.values(variants).find(
        variant => variant.catalog_number === urlParams.get("v")
      );

      defaultVariant = selectedVariant || defaultVariant;
    }

    const settings = {
      height: artworkHeight ? Number(artworkHeight) : GANG_DEFAULT_VALUES.height * INCHES_PER_FOOT,
      variantId: defaultVariant.variant_id
    };

    store.updateGangSheetBuilderSettings(settings);
    store.setIsGangSheetBuilder(isGangSheetBuilder === "true");
    store.setGangSheetBuilderVariants(variants);
    store.setVariantId(defaultVariant.variant_id);
    store.setProductSlug(defaultVariant.product_slug);
    store.setGangSheetBuilderCarousels(JSON.parse(gangSheetBuilderCarousels));
  }

  if (buyAsStickerArtworkIds) {
    const artworkIds = JSON.parse(decodeURIComponent(buyAsStickerArtworkIds));
    store.buyAsStickerById({ artworkIds });
  }

  if (rawArtworkIds) {
    const artworkIds = JSON.parse(decodeURIComponent(rawArtworkIds));
    const artworkUrls = JSON.parse(rawArtworkUrls);

    artworkIds.forEach((artworkId, index) => {
      store.setEmptyArtwork(artworkId);
      store.updateArtworkValuesById({
        id: artworkId,
        progress: 25,
        progressMessage: "Processing file",
        url: artworkUrls?.[artworkId],
        isFile: true,
        processTimeStamp: Date.now(),
        width: artworkWidth,
        height: artworkHeight,
        originalIndex: index
      });
    });
    options = {
      artworkIds,
      artworkWidth,
      artworkHeight,
      processArtwork,
      urlPreCut,
      fileContentType,
      artworkPaddingSize,
      artworkBgColor,
      artworkColor,
      artworkShape
    };

    store.setActionCableOptions(options);
  }

  if (sticker && sourceParam === "gangsheet") {
    if (userType === "signed_in") {
      store.setShowArtworkHistoryModal(true);
      store.setStickerQrScanned(true);
    }

    track("sticker_qr_scanned_on_gangsheets", {});
  }

  if (jerseyNumbers && letterNumberHeight && letterNumberStyle && letterNumberColor) {
    store.setLoadFromConfig(false);
    store.updateArtworkValues({
      index: 0,
      height: Number(letterNumberHeight),
      color: letterNumberColor,
      style: letterNumberStyle
    });
    const isPreCut = urlPreCut === "true";
    store.setPreCut(isPreCut);
    store.setEnablePreCut();
  }

  if (
    isLetters &&
    letterNumberColor &&
    letterNumberStyle &&
    letterNumberMaxLength &&
    letterNumberHeight &&
    letterNumberFont
  ) {
    store.setLoadFromConfig(false);
    store.updateArtworkValues({
      index: 0,
      height: Number(letterNumberHeight),
      color: letterNumberColor,
      style: letterNumberStyle,
      maxLength: Number(letterNumberMaxLength),
      font: letterNumberFont
    });
    const isPreCut = urlPreCut === "true";
    store.setPreCut(isPreCut);
    store.setEnablePreCut();
  }

  if (orderNumber) {
    store.buyArtworksAgainFromOrder(orderNumber);
  }

  if (designId) {
    store.setPreloadedDesignId(designId);
  }

  if (trackSpecificStickerLink) {
    track("dont_forget_stickers_email_specific_sticker_link", {
      link_number: trackSpecificStickerLink
    });
  }

  if (trackOrderStickersLink) {
    track("dont_forget_stickers_email_order_stickers_link", {
      link_number: trackOrderStickersLink
    });
  }

  // Disable scroll restoration to improve CLS on pages where vue product details is used
  if (window.history.scrollRestoration) {
    window.history.scrollRestoration = "manual";
  }

  ProductDetailsDtfWrapper.directive("click-outside", clickOutside());
  ProductDetailsDtfWrapper.mount("#vue-product-details-dtf");
};
/* eslint-enable max-lines-per-function */
/* eslint-enable max-lines */

export default initProductDetailsDtf;
